import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { LoadPanel, Popup } from "devextreme-react";
import { DynamicTabb } from "../../../components/DynamicTab/DynamicTab";
import { handleGetLocal } from "../../../services/auth-service";
import { logOut } from "../../../components/Logout";
import { useDispatch } from "react-redux";
import { removeDynamicTab } from "../../../reducer/HomePageReducer";
import { Table2 } from "../../../components/Table2/Table2";
import { Toster } from "../../../components/Toster/Toster";
import VoidCloseoutPopup from "./VoidCloseoutPopup";
import { useScreenPermission } from "../../../hooks/useScreenPermission";

export const ProcessPersonCloseout = () => {
  const { taskId } = useParams();

  const [loading, setLoading] = useState(false);
  const [closeoutData, setCloseoutData] = useState(null);

  const [toastConfig, setToastConfig] = useState({
    isVisible: false,
    type: "",
    message: "",
  });

  const navigate = useNavigate();
  const SCREEN_NAME = "PS11";
  const { permission, permissionLoading } = useScreenPermission(SCREEN_NAME, navigate);
  const dispatch = useDispatch();
  const pathname = handleGetLocal("pathURL");
  const [voidPopup, setVoidPopup] = useState(false);

  const tableColumns = [
    {
      title: "Item",
    },
    {
      title: "Description",
      customwidth: "250px",
    },
    {
      title: "Action",
      customwidth: "250px",
    },
    {
      title: "Amount",
      number: true,
      editable: false,
      currency: true,
    },
  ];

  const loadCloseout = () => {
    if (!taskId) return;

    setLoading(true);

    axios
      .get(`${process.env.REACT_APP_API_URL}/api/close-out-wizard/pending-close-out?taskId=${taskId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          Domain: `${pathname}`,
        },
      })
      .then((response) => {
        setLoading(false);

        if (response?.data?.isSuccess) {
          const data = response?.data?.data;

          setCloseoutData({
            ...data,
            items:
              data?.items?.map((item) => ({
                ID: item?.closeoutActionId,

                Item: item?.item,
                Description: item?.description,
                Action: item?.action,
                Amount: item?.amount,

                closeoutActionId: item?.closeoutActionId,
                recordId: item?.recordId,
              })) || [],
          });
        } else {
          setToastConfig({
            isVisible: true,
            type: "error",
            message: response?.data?.message || "Unable to load pending closeout.",
          });
        }
      })
      .catch((error) => {
        setLoading(false);

        if (error?.response?.status === 401) {
          logOut();
        } else {
          navigate("/error/500");
        }
      });
  };

  const handleApprove = () => {
    if (!closeoutData?.closeoutId) {
      setToastConfig({
        isVisible: true,
        type: "error",
        message: "Closeout ID was not found.",
      });

      return;
    }

    setLoading(true);

    axios
      .post(
        `${process.env.REACT_APP_API_URL}/api/close-out-wizard/approve-close-out?closeoutId=${closeoutData.closeoutId}`,
        null,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            Domain: `${pathname}`,
          },
        },
      )
      .then((response) => {
        setLoading(false);

        if (response?.data?.success) {
          setToastConfig({
            isVisible: true,
            type: "success",
            message: response?.data?.message || "Closeout approved successfully.",
          });

          setTimeout(() => {
            handleCancel();
          }, 1500);
        } else {
          setToastConfig({
            isVisible: true,
            type: "error",
            message: response?.data?.message || "Closeout approval failed.",
          });
        }
      })
      .catch((error) => {
        setLoading(false);

        if (error?.response?.status === 401) {
          logOut();
        } else {
          navigate("/error/500");
        }
      });
  };

  const handleReject = () => {
    setVoidPopup(true);
  };

  const handleCancel = () => {
    dispatch(removeDynamicTab(`/process-person-closeout/${taskId}`));

    navigate("/tasks");
  };

  useEffect(() => {
    if (permissionLoading || !permission.view) return;

    loadCloseout();
  }, [taskId, permissionLoading, permission.view]);

  return (
    <div className="page-section">
      <Popup
        visible={voidPopup}
        onHiding={() => setVoidPopup(false)}
        showTitle={false}
        showCloseButton={false}
        hideOnOutsideClick={false}
        width={800}
        height={450}
      >
        <VoidCloseoutPopup
          setVoidPopup={setVoidPopup}
          closeoutId={closeoutData?.closeoutId}
          taskId={taskId}
          setToastConfig={setToastConfig}
          toastConfig={toastConfig}
          onSuccess={handleCancel}
        />
      </Popup>
      <DynamicTabb />

      <LoadPanel shadingColor="true" visible={loading} delay={10} />

      <Toster message={toastConfig.message} type={toastConfig.type} visible={toastConfig.isVisible} />
      {permissionLoading ? (
        <LoadPanel shadingColor="true" visible={true} delay={10} />
      ) : !permission.view ? (
        <div style={{ padding: "20px", fontWeight: 600 }}>
          You do not have View access for PS.11. Please contact your system administrator.
        </div>
      ) : (
        <div className="page-content">
          <p className="page-info-number">PS.11</p>

          <div
            style={{
              marginBottom: "15px",
            }}
          >
            <div>
              <strong>Requested By:</strong> {closeoutData?.requestedBy || ""}
            </div>

            <div>{closeoutData?.note || ""}</div>
          </div>

          <div className="page-info-buttons">
            <div className="buttons">
              <button
                className="btn primary_btn"
                onClick={handleApprove}
                disabled={loading || !closeoutData?.closeoutId}
              >
                Approve and Process
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M5 13L10 18L20 6"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              <button className="btn" onClick={handleReject} disabled={loading || !closeoutData?.closeoutId}>
                Reject and Void
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M6 6L18 18M18 6L6 18" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                </svg>
              </button>

              <button className="btn" onClick={handleCancel}>
                Cancel
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="2.5" />

                  <path d="M7 17L17 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          </div>

          <div
            className="table-section"
            style={{
              marginBottom: "20px",
            }}
          >
            <Table2
              Tablecoloum={tableColumns}
              tableRow={closeoutData?.items || []}
              editTable={false}
              load={loading}
              height={450}
              mandatoryColoum={"Action"}
              processpersoncloseout={true}
              showTotalBlock={true}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ProcessPersonCloseout;
